import React, { useState, useEffect, FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { makeApiCall } from "src/api/apiRequests";
import { LanguageDate } from "src/helpers/CheckLanguageDate";
import CustomPagination from "src/helpers/CustomPaginate";
import API_PATHS from "src/api/apiPaths";
import API_HEADERS from "src/api/apiConfig";
import SearchFilter from "src/helpers/SearchFilter";
import ListResponse from "src/helpers/ListResponse";
import FLASH_MESSAGES from "src/helpers/FlashMessages";
import Loading from "src/helpers/Loading";
import DatePicker from "react-datepicker";
import { PermissionCheck } from "src/helpers/PermissionCheck";
import CheckMonth from "src/helpers/CheckMonth";
import ValidationMessage from "src/helpers/ValidationMessage";

const FinanceList: React.FC = () => {
  const { t } = useTranslation("");
  const navigate = useNavigate();

  const getPreviousMonth = () => {
    const today = new Date();
    const previousMonth = new Date(
      today.getFullYear(),
      today.getMonth() - 1,
      1
    );
    return previousMonth;
  };

  const [invoiceList, setInvoiceList] = useState<any>([]);
  const [activePage, setActivePage] = useState<number>(1);
  const [searchBtnLoading, setSearchBtnLoading] = useState(false);
  const [childName, setChildName] = useState<string>("");
  const [validations, setValidations] = useState<Record<string, string>>({});
  const [date, setDate] = useState(getPreviousMonth());
  const [loading, setLoading] = useState<boolean>(true);

  const handleDate = (date: Date) => {
    if (date !== null) {
      setDate(date);
    } else {
      setDate(new Date());
    }
  };

  const getFinanceList = async (e?: FormEvent<HTMLElement>): Promise<void> => {
    e && e.preventDefault();
    e && setActivePage(1);

    var formData: any = {
      month: date.getMonth() + 1,
      year: date.getFullYear(),
    };

    childName && (formData["child_name"] = childName);

    let request: any = API_PATHS.financeList;

    let searchParams: any = {};

    if (activePage > 1) {
      searchParams["page"] = activePage;
    }

    request = SearchFilter(searchParams, request);

    try {
      const response: any = await makeApiCall<ResponseType>(
        request,
        "POST",
        API_HEADERS.authenticated,
        formData
      );

      setInvoiceList(response.invoices);
      setLoading(false);
    } catch (error: any) {
      if (error.response.status === 422) {
        setValidations(error.response.data);
      } else {
        FLASH_MESSAGES.errorMsg(error.response.data.error);
        navigate(-1);
      }
    } finally {
      setSearchBtnLoading(false);
    }
  };

  useEffect(() => {
    getFinanceList();
  }, [activePage]);

  const regenerateInvoice = async (invoiceId: number): Promise<void> => {
    try {
      const response: any = await makeApiCall<ResponseType>(
        API_PATHS.regenerateInvoice(Number(invoiceId)),
        "POST",
        API_HEADERS.authenticated
      );

      FLASH_MESSAGES.successMsg(response.success);
    } catch (error: any) {
      if (error.response.status === 422) {
        setValidations(error.response.data);
      } else {
        FLASH_MESSAGES.errorMsg(error.response.data.error);
      }
    }
  };

  return (
    <>
      <div className="input-items">
        <form onSubmit={(e) => getFinanceList(e)} className="row mb-4">
          <div className="form-block col-12  col-sm-6 col-md-3">
            <label>{t("finance_choose_year")}</label>
            <DatePicker
              selected={date}
              onChange={handleDate}
              autoComplete="off"
              popperClassName="navigations"
              showMonthYearPicker
              dateFormat="MMMMMM/yyyy"
              placeholderText={t("finance_choose_year")}
              className={
                "form-datepicker" +
                (validations.month || validations.year ? " input-error" : "")
              }
              name="month_year"
              maxDate={getPreviousMonth()}
              locale={LanguageDate.datepicker}
              required={true}
            />
            {(validations.month || validations.year) && (
              <ValidationMessage
                message={validations.month[0] || validations.year[0]}
              />
            )}
          </div>
          <div className="form-block col-12 col-sm-6 col-md-3">
            <label>{t("finance_search_child_name")}</label>
            <input
              placeholder={t("finance_search")}
              className="form-input"
              name="text"
              type="text"
              onChange={(e) => setChildName(e.target.value)}
            />
          </div>
          <div className="col-12 col-sm-6 col-md-3 mb-1 mt-4">
            {searchBtnLoading ? (
              <button type="button" className="button-orange has-icon">
                <i className="icon spinner-icon" />
                {t("finance_searching")}
              </button>
            ) : (
              <button type="submit" className="button-orange has-icon">
                <i className="icon search-icon" />
                {t("finance_search")}
              </button>
            )}
          </div>
        </form>
      </div>
      <div className="table-list mb-5">
        <table role="table" className="w-100">
          <thead>
            <tr role="row">
              <th
                style={{ width: "16%" }}
                role="columnheader"
                className="text-left"
              >
                <i className="icon sidebar-children-categories-icon" />
                {t("finance_invoice_number")}
              </th>
              <th
                style={{ width: "16%" }}
                role="columnheader"
                className="text-center"
              >
                <i className="icon child-icon" />
                {t("finance_firstname_lastname")}
              </th>
              <th
                style={{ width: "16%" }}
                role="columnheader"
                className="text-center"
              >
                <i className="icon kindergarten-table-icon" />
                {t("finance_kindergarten_name")}
              </th>
              <th
                style={{ width: "16%" }}
                role="columnheader"
                className="text-center"
              >
                <i className="icon calender-icon" />
                {t("finance_month")}
              </th>
              <th
                style={{ width: "16%" }}
                role="columnheader"
                className="text-center"
              >
                <i className="icon calender-icon" />
                {t("finance_year")}
              </th>
              <th
                style={{ width: "16%" }}
                role="columnheader"
                className="text-center"
              >
                <i className="icon actions-icon" />
                {t("finance_action")}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className="text-center" colSpan={6}>
                  <Loading />
                </td>
              </tr>
            ) : (
              invoiceList.data.map((invoice: any) => (
                <tr key={invoice.id}>
                  <td
                    role="cell"
                    className="text-left"
                    data-label={t("finance_invoice_number")}
                  >
                    {invoice.invoice_number ? invoice.invoice_number : "-"}
                  </td>
                  <td
                    role="cell"
                    className="text-center"
                    data-label={t("finance_firstname_lastname")}
                  >
                    {(invoice.child.first_name
                      ? invoice.child.first_name
                      : "-") +
                      " " +
                      (invoice.child.last_name ? invoice.child.last_name : "-")}
                  </td>
                  <td
                    role="cell"
                    className="text-center"
                    data-label={t("finance_kindergarten_name")}
                  >
                    {invoice.kindergarten.name
                      ? invoice.kindergarten.name
                      : "-"}
                  </td>
                  <td
                    role="cell"
                    className="text-center"
                    data-label={t("finance_month")}
                  >
                    {invoice.month ? CheckMonth(invoice.month) : "-"}
                  </td>
                  <td
                    role="cell"
                    className="text-center"
                    data-label={t("finance_year")}
                  >
                    {invoice.year ? invoice.year : "-"}
                  </td>
                  <td
                    role="cell"
                    className="text-center"
                    data-label={t("finance_action")}
                  >
                    <div className="actions">
                      {PermissionCheck("finance-regenerate") &&
                      invoice.price_type === "daily" ? (
                        <button
                          type="button"
                          className="button-unstyled-icon"
                          onClick={() => regenerateInvoice(invoice.id)}
                        >
                          <i className="icon regenerate-icon" />
                        </button>
                      ) : (
                        "-"
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        <ListResponse totalItems={invoiceList?.data?.length} loading={loading} />
      </div>
      <CustomPagination
        data={invoiceList}
        setActivePage={(e) => setActivePage(e)}
      />
    </>
  );
};

export default FinanceList;
